import React, { useState, useEffect, useContext, useRef } from "react";
import { Icon, Button, Form, Modal, Input, Dropdown } from "semantic-ui-react";
import SemanticDatepicker from "react-semantic-ui-datepickers";
import { useToasts } from "react-toast-notifications";
import { AppContext } from "../../../../Context/AppContext";
import moment from "moment";
import MDL_PROGRESS from "../../../../components/cmp-progress/cmp-progress";
import {
  getBuildingLookup,
  getCriticalUnitServiceDetails,
  UpdateCriticalUnitData,
  deleteCriticalUnit,
  getDeleteReasons,
  getPestLookup,
  getIssueTagsLookup,
} from "../../../../constants";
import MDL_DELETE_CRITICAL_UNIT from "../mdl-delete-critical-unit/mdl-delete-critical-unit";
import "./mdl-update-critical-unit.css";
const MDL_UPDATE_CRITICAL_UNIT = ({
  set_mdl_open,
  SingleCriticalData,
  setPage,
  onListRefresh,
}) => {
  /* VARIABLE DECLARATIONS
	---------------------------------------------------------------------------------*/
  const { userInfo, appVersion, isAppVersionOutdated, criticalUnitDeletion } =
    useContext(AppContext);
  const [user] = userInfo;
  const [var_app_version] = appVersion;
  const [, set_is_app_version_outdated] = isAppVersionOutdated;
  const [, setIsCriticalUnitDeleted] = criticalUnitDeletion;
  const [building_units, setBuildingUnits] = useState([]);
  const [pestData, setPestData] = useState([]);
  const [buildingInfo, setBuildingInfo] = useState([]);
  const [selected_building, setSelectedBuilding] = useState([]);
  const [ServiceDate, setNewServiceDate] = useState(
    moment(SingleCriticalData.last_service_date).toDate(),
  );
  const [currentDate, setNewDate] = useState(
    moment(SingleCriticalData.date_reported).toDate(),
  );
  const [errors, set_errors] = useState({});
  const [isLoading, setLoading] = useState(false);
  const { addToast } = useToasts();
  const [var_mdl_progress, set_mdl_progress] = useState(false);
  const [var_critical_info, set_critical_info] = useState(SingleCriticalData);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteReasons, setDeleteReasons] = useState([]);
  const [issueTagsOptions, setIssueTagsOptions] = useState([]);
  const [SelectedPestData, setSelectedPestData] = useState(
    SingleCriticalData?.pest_name,
  );
  const currentStatus = SingleCriticalData?.status;
  const buildingFieldRef = useRef(null);
  const unitFieldRef = useRef(null);
  const pestFieldRef = useRef(null);
  const [activeFloatingField, setActiveFloatingField] = useState(null);

  // The dropdown menu sits inside the scrollable modal content, so a high
  // z-index alone can't lift it above the fold — the container still clips/
  // scrolls it. Instead, once open, pin the menu with position:fixed at the
  // field's on-screen coordinates so it floats above the whole modal,
  // unaffected by the container's scroll or stacking.
  function positionFloatingMenu(fieldRef) {
    const container = fieldRef?.current;
    if (!container) return;
    const dropdownEl = container.querySelector(".ui.dropdown");
    const menuEl = dropdownEl?.querySelector(":scope > .menu");
    if (!dropdownEl || !menuEl) return;
    const rect = dropdownEl.getBoundingClientRect();
    menuEl.style.position = "fixed";
    menuEl.style.top = `${rect.bottom}px`;
    menuEl.style.left = `${rect.left}px`;
    menuEl.style.width = `${rect.width}px`;
    menuEl.style.setProperty("z-index", "99999", "important");
  }

  function resetFloatingMenu(fieldRef) {
    const container = fieldRef?.current;
    const menuEl = container?.querySelector(".ui.dropdown > .menu");
    if (!menuEl) return;
    menuEl.style.position = "";
    menuEl.style.top = "";
    menuEl.style.left = "";
    menuEl.style.width = "";
    menuEl.style.removeProperty("z-index");
  }

  function handleFloatingDropdownOpen(fieldRef) {
    setActiveFloatingField(fieldRef);
    requestAnimationFrame(() => positionFloatingMenu(fieldRef));
  }

  function handleFloatingDropdownClose(fieldRef) {
    resetFloatingMenu(fieldRef);
    setActiveFloatingField(null);
  }

  // Keep the floated menu aligned with its field if the modal (or page)
  // scrolls or the window resizes while the menu is open.
  useEffect(() => {
    if (!activeFloatingField) return;
    const reposition = () => positionFloatingMenu(activeFloatingField);
    document.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      document.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [activeFloatingField]);

  /* EVENT LISTENERS
	---------------------------------------------------------------------------------*/
  useEffect(() => {
    pestLookup();
    loadSelectedBuilding();
    getIssueTagsOptions();
    if (SingleCriticalData.status === "ongoing") getDeleteOptions();
  }, []);

  /* ASYNC FUNCTIONS
	---------------------------------------------------------------------------------*/
  async function loadSelectedBuilding() {
    const buildingId = SingleCriticalData?.buildingID;
    if (!buildingId) {
      return;
    }

    setLoading(true);
    set_mdl_progress(true);
    const requestHeaders = {
      headers: {
        entityid: user.uuid,
        appversion: var_app_version,
      },
    };
    try {
      // Load by id so units are included (search/list responses omit units)
      const response = await fetch(
        getBuildingLookup + `?id=${encodeURIComponent(buildingId)}`,
        requestHeaders,
      );
      if (!response.ok) {
        throw new Error("Failed to fetch building info");
      }
      const data = await response.json();
      if (data.code == 200 && data.data?.[0]) {
        const building = data.data[0];
        const formattedBuilding = {
          key: building.value,
          value: building.value,
          text: `${building.text} (${building.location_number})`,
          customer_name: building.customer_name || "",
          units: (building.units || []).map((unit) => ({
            key: unit.value,
            value: unit.value,
            text: unit.text,
          })),
        };
        setBuildingInfo([formattedBuilding]);
        setSelectedBuilding(formattedBuilding);

        const unitNumber = String(
          SingleCriticalData?.unit_number ??
            var_critical_info?.unit_number ??
            "",
        );
        const matchedUnit = formattedBuilding.units.find(
          (item) => String(item.text) === unitNumber,
        );
        if (matchedUnit) {
          setBuildingUnits(matchedUnit.value);
        }
      } else {
        if (data?.version && data?.version !== var_app_version) {
          set_is_app_version_outdated(true);
        }
        console.log("Else data:::", data.message);
      }
    } catch (error) {
      console.error("Error fetching building info:", error);
    } finally {
      setLoading(false);
      set_mdl_progress(false);
    }
  }

  async function fetchBuildingInfo(searchQuery) {
    setLoading(true);
    const requestHeaders = {
      headers: {
        entityid: user.uuid,
        appversion: var_app_version,
      },
    };
    try {
      const queryValue = searchQuery ? encodeURIComponent(searchQuery) : "";
      const response = await fetch(
        getBuildingLookup + `?q=${queryValue}`,
        requestHeaders,
      );
      if (!response.ok) {
        throw new Error("Failed to fetch building info");
      }
      const data = await response.json();
      if (data.code == 200) {
        const formattedBuilding = data.data.map((building) => ({
          key: building.value,
          value: building.value,
          text: `${building.text} (${building.location_number})`,
          customer_name: building.customer_name || "",
          units: building.units || [],
        }));
        setBuildingInfo(formattedBuilding);
      } else {
        if (data?.version && data?.version !== var_app_version) {
          set_is_app_version_outdated(true);
        }
        console.log("Else data:::", data.message);
      }
    } catch (error) {
      console.error("Error fetching building info:", error);
    } finally {
      setLoading(false);
    }
  }

  async function getUnitdata(unit) {
    setLoading(true);
    set_mdl_progress(true);
    try {
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Token " + user.token,
          entityid: user.uuid,
          appversion: var_app_version,
        },
      };
      const response = await fetch(
        getCriticalUnitServiceDetails +
          `?unitNumber=${
            selected_building?.units.find(
              (item) => item.value === unit.toString(),
            )?.text
          }&locationID=${selected_building?.value}`,
        requestOptions,
      );
      if (!response.ok) {
        throw new Error("Failed to fetch unit info");
      }
      const data = await response.json();
      if (data.code === 200) {
        setNewServiceDate(
          data.data.last_service_date
            ? moment(data.data.last_service_date).toDate()
            : null,
        );
        const pestName = data.data?.pest_name;
        setSelectedPestData(
          pestName && pestName !== "" && pestName !== null
            ? pestData?.find((pest) => pest.text === pestName)?.value || null
            : "N/A",
        );
        // Update var_critical_info with the new data, ensuring state merges correctly
        set_critical_info((prevState) => ({
          ...prevState,
          // tech_reported: data.data.tech_reported || "",
          last_tech_comment: data.data.last_tech_comment || "",
        }));
      } else {
        if (data?.version && data?.version !== var_app_version) {
          set_is_app_version_outdated(true);
        }
        setSelectedPestData(null);
        set_critical_info((prevState) => ({
          ...prevState,
          // tech_reported: "",
          last_service_date: null,
          last_tech_comment: "",
        }));
        console.log("Error:", data.message);
      }
      setLoading(false);
      set_mdl_progress(false);
    } catch (error) {
      setLoading(false);
      set_mdl_progress(false);
      console.error("Fetch error:", error);
    }
  }

  async function getDeleteOptions() {
    setLoading(true);
    set_mdl_progress(true);
    try {
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Token " + user.token,
          entityid: user.uuid,
          appversion: var_app_version,
        },
      };
      const response = await fetch(getDeleteReasons, requestOptions);
      if (!response.ok) {
        throw new Error("Failed to fetch unit info");
      }
      const data = await response.json();
      if (data.code === 200) {
        setDeleteReasons(data.data);
      } else {
        if (data?.version && data?.version !== var_app_version) {
          set_is_app_version_outdated(true);
        }
      }
      setLoading(false);
      set_mdl_progress(false);
    } catch (error) {
      setLoading(false);
      set_mdl_progress(false);
      console.error("Fetch error:", error);
    }
  }

  async function getIssueTagsOptions() {
    setLoading(true);
    set_mdl_progress(true);
    try {
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Token " + user.token,
          entityid: user.uuid,
          appversion: var_app_version,
        },
      };
      const response = await fetch(getIssueTagsLookup, requestOptions);
      if (!response.ok) {
        throw new Error("Failed to fetch issue tags lookup");
      }
      const data = await response.json();
      if (data.code === 200) {
        setIssueTagsOptions(data.data);
      } else {
        if (data?.version && data?.version !== var_app_version) {
          set_is_app_version_outdated(true);
        }
      }
      setLoading(false);
      set_mdl_progress(false);
    } catch (error) {
      setLoading(false);
      set_mdl_progress(false);
      console.error("Fetch error:", error);
    }
  }

  async function pestLookup() {
    setLoading(true);
    set_mdl_progress(true);
    const requestHeaders = {
      headers: {
        entityid: user.uuid,
        appversion: var_app_version,
      },
    };
    await fetch(getPestLookup, requestHeaders)
      .then((response) => response.json())
      .then((res) => {
        if (res.code === 200) {
          setPestData(res?.data);
          setSelectedPestData(
            res?.data?.find(
              (pest) => pest.text === SingleCriticalData?.pest_name,
            )?.value || null,
          );
        } else {
          if (res?.version && res?.version !== var_app_version) {
            set_is_app_version_outdated(true);
          }
          setPestData(null);
        }
        setLoading(false);
        set_mdl_progress(false);
      })
      .catch((error) => {
        setLoading(false);
        set_mdl_progress(false);
        setPestData(null);
        console.error("Error:", error);
      });
  }

  /* EVENT FUNCTIONS
	---------------------------------------------------------------------------------*/

  function isValidated() {
    var temp_error = {};
    var isValid = true;
    if (!currentDate) {
      temp_error.dateReportedDate = "Please select date.";
      isValid = false;
    }
    if (!selected_building?.value) {
      temp_error.selected_building = "Please select building.";
      isValid = false;
    }
    if (
      !building_units ||
      (Array.isArray(building_units) && building_units.length === 0)
    ) {
      temp_error.buildingunits = "Please select units.";
      isValid = false;
    }

    // const summaryValue = (var_critical_info?.summary || "").trim();
    // if (!summaryValue) {
    //   temp_error.summary = "Please add summary.";
    //   isValid = false;
    // } else if (summaryValue.length > 2000) {
    //   temp_error.summary = "Summary Character limit exceeded.";
    //   isValid = false;
    // }

    if (!ServiceDate) {
      temp_error.lastScheduledDate = "Please select service date.";
      isValid = false;
    }

    // Note is optional — only validate character limit when note has content
    const noteValue = var_critical_info?.note || "";
    if (noteValue.length > 2000) {
      temp_error.note = "Note Character limit exceeded.";
      isValid = false;
    }

    const lastTechValue = (var_critical_info?.last_tech_comment || "").trim();
    if (!lastTechValue) {
      temp_error.last_tech_comment = "Please add last tech comment.";
      isValid = false;
    } else if (lastTechValue.length > 1000) {
      temp_error.last_tech_comment =
        "Last Tech Comment Character limit exceeded.";
      isValid = false;
    }

    if (isValid) {
      set_errors({});
    } else {
      set_errors(temp_error);
    }
    return isValid;
  }

  function onChangeUnits(event, { value }) {
    // var temp_error = { ...errors };
    // temp_error.buildingunits = null;
    // set_errors(temp_error);
    // const filteredArray = selected_building?.units?.filter(unit => value.includes(unit.value));
    getUnitdata(value);
    setBuildingUnits(value);
  }

  const convertDateFormat = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString();
  };

  const onChange = (event, data) => {
    if (data?.value) {
      try {
        // Parse the date using moment and convert it to a Date object
        const parsedDate = moment(data.value).toDate();

        // If the date is valid, update the state and reset the error
        if (!isNaN(parsedDate.getTime())) {
          var temp_error = { ...errors };
          temp_error.currentDate = null; // Reset the specific error
          set_errors(temp_error);
          setNewDate(parsedDate); // Set the date directly as a Date object
        } else {
          throw new Error("Invalid date");
        }
      } catch (error) {
        console.error("Date parsing error:", error.message);
      }
    }
  };

  const onChangeLastServiceDate = (event, data) => {
    if (data?.value) {
      try {
        // Parse the date using moment and convert it to a Date object
        const parsedDate = moment(data.value).toDate();

        // If the date is valid, update the state and reset the error
        if (!isNaN(parsedDate.getTime())) {
          var temp_error = { ...errors };
          temp_error.lastScheduledDate = null; // Reset the specific error
          set_errors(temp_error);
          setNewServiceDate(parsedDate); // Set the date directly as a Date object
        } else {
          throw new Error("Invalid date");
        }
      } catch (error) {
        console.error("Date parsing error:", error.message);
      }
    }
  };

  // const onChangeInput = (event) => {
  //   let name = event.target.name;
  //   let value = event.target.value;
  // }

  function handleSearchChange(event, data) {
    const { searchQuery } = data;
    fetchBuildingInfo(searchQuery);
  }

  function onChangeBuilding(event, { value }) {
    var temp_error = { ...errors };
    temp_error.selected_building = null;
    set_errors(temp_error);
    setSelectedBuilding(buildingInfo.filter((f_v) => f_v.value === value)[0]);
  }

  const FIELD_CHAR_LIMITS = {
    summary: 2000,
    note: 2000,
    last_tech_comment: 1000,
    csr_actions: 500,
    ss_rm_steps: 500,
  };

  const onChangeInput = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    const limit = FIELD_CHAR_LIMITS[name] ?? 2000;

    // Only show character-limit error when text actually reaches/exceeds the limit
    if (value.length > 0 && value.length >= limit) {
      set_errors((prevErrors) => ({
        ...prevErrors,
        [name]: "Character limit exceeded",
      }));
    } else {
      set_errors((prevErrors) => {
        const nextErrors = { ...prevErrors };
        if (
          nextErrors[name] === "Character limit exceeded" ||
          nextErrors[name]?.includes("Character limit exceeded")
        ) {
          delete nextErrors[name];
        }
        return nextErrors;
      });
    }

    set_critical_info({ ...var_critical_info, [name]: value });
  };

  const renderCharLimitHint = (fieldName) => {
    const limit = FIELD_CHAR_LIMITS[fieldName] ?? 2000;
    const length = var_critical_info?.[fieldName]?.length || 0;
    const remaining = Math.max(0, limit - length);
    const isLimitExceeded = length >= limit;

    return (
      <div
        className={fieldName === "last_tech_comment" ? "char-limit-hint" : undefined}
        style={{
          textAlign: "right",
          color: isLimitExceeded ? "red" : "gray",
        }}
      >
        {isLimitExceeded
          ? "Character limit exceeded"
          : `${remaining} characters left`}
      </div>
    );
  };

  async function handleDeleteCriticalUnit(payload) {
    set_mdl_progress(true);
    const requestOptions = {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Token " + user.token,
        entityid: user.uuid,
        appversion: var_app_version,
      },
    };
    var apiUrl =
      deleteCriticalUnit +
      `${var_critical_info?.uuid}?reason=${encodeURIComponent(
        payload?.deleteReason.toLowerCase() === "other"
          ? payload.otherReason
          : payload.deleteReason,
      )}`;
    await fetch(apiUrl, requestOptions)
      .then((response) => response.json())
      .then((res) => {
        const { success, code } = res;
        if (code === 200) {
          setIsCriticalUnitDeleted(true);
          addToast(success, {
            appearance: "success",
            autoDismiss: true,
          });
          //get_all_critical_list();
        } else {
          if (res?.version && res?.version !== var_app_version) {
            set_is_app_version_outdated(true);
          }
          addToast(res?.failed, {
            appearance: "error",
            autoDismiss: true,
          });
        }
      })
      .catch((error) => {
        console.error("Error:", error);
      });
    set_mdl_open(false);
    set_mdl_progress(false);
  }

  function handleResolveCriticalUnit(data) {
    if (isValidated()) {
      var_critical_info.status = "resolved";
    }
    UpdateCriticalData();
    //addToast("Critical Unit Resolved", { appearance: "success" });
  }

  function handleUpdateCriticalUnitStatus() {
    if (isValidated()) {
    var_critical_info.status = "ongoing";
      UpdateCriticalData();
    }
  }

  function handleSendToSupervisor() {
    if (isValidated()) {
      var_critical_info.status = "supervisor";
      UpdateCriticalData();
    }
  }

  async function UpdateCriticalData() {
    if (currentStatus === "resolved" || isValidated()) {
      var_critical_info.buildingID =
        selected_building?.value || SingleCriticalData?.buildingID;
      var_critical_info.building_address =
        selected_building?.text || SingleCriticalData?.building_address;
      const matchedUnit = selected_building?.units?.find(
        (item) => item.value === building_units?.toString(),
      );
      var_critical_info.unit_number =
        matchedUnit?.text ||
        SingleCriticalData?.unit_number ||
        var_critical_info.unit_number;
      var_critical_info.date_reported = convertDateFormat(currentDate).replace(
        "Z",
        "",
      );
      var_critical_info.last_service_date = convertDateFormat(
        ServiceDate,
      ).replace("Z", "");
      set_mdl_progress(true);
      const requestWarranty = {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Token " + user.token,
          entityid: user.uuid,
          appversion: var_app_version,
        },
        body: JSON.stringify(var_critical_info),
      };
      await fetch(
        UpdateCriticalUnitData + var_critical_info?.uuid,
        requestWarranty,
      )
        .then((response) => response.json())
        .then((res) => {
          if (res.code == 200) {
            addToast(res.success, {
              appearance: "success",
              autoDismiss: true,
            });
            if (typeof onListRefresh === "function") {
              onListRefresh();
            } else {
              setPage(1);
            }
            //get_all_critical_list();
          } else {
            if (res?.version && res?.version !== var_app_version) {
              set_is_app_version_outdated(true);
            }
            addToast(res.failed, {
              appearance: "error",
              autoDismiss: true,
            });
          }
        })
        .catch((error) => {
          console.log("Errors:", error);
        });
      set_mdl_open(false);
      set_mdl_progress(false);
    }
  }

  function onChangePest(event, data) {
    let id = data.value;
    let name = data.name;
    let value = data.options?.find((opt) => opt.value === id)?.text;
    var temp_error = { ...errors };
    temp_error.pest = null;
    set_errors(temp_error);
    setSelectedPestData(id);
    set_critical_info({ ...var_critical_info, [name]: value });
  }

  function onChangeIssueTags(event, { value }) {
    set_critical_info({ ...var_critical_info, issue_tags: value });
  }

  /* RENDER APP
	---------------------------------------------------------------------------------*/
  return (
    <>
      <div className="modal-header">
        <div className="modal-header-title">Update Critical Unit Details</div>
        <div className="modal-header-close" onClick={() => set_mdl_open(false)}>
          <Icon name="times circle outline" />
        </div>
      </div>
      <div className="modal-content fields critical-update-fields">
        <div className="modal-field" ref={buildingFieldRef}>
          <label htmlFor="building_dropdown">
            Location ID or Building Address:{" "}
          </label>
          <Dropdown
            search
            selection
            className="building_dropdown"
            id="building_dropdown"
            name="sortby_options"
            placeholder="Select Building"
            label="Building Address: "
            onSearchChange={handleSearchChange}
            onOpen={() => handleFloatingDropdownOpen(buildingFieldRef)}
            onClose={() => handleFloatingDropdownClose(buildingFieldRef)}
            onChange={onChangeBuilding}
            value={selected_building?.value}
            options={buildingInfo}
            error={errors.selected_building}
            disabled={true}
            selectOnBlur={false}
          />
        </div>
        <div className="modal-field">
          <label htmlFor="client">Client: </label>
          <Form.Field
            id="client"
            required={true}
            name="client"
            placeholder="Client"
            style={{ width: "100%" }}
            disabled={true}
            control={Input}
            value={selected_building?.customer_name}
          />
        </div>
        <div className="modal-field" ref={unitFieldRef}>
          <label htmlFor="sortby_units">Unit: </label>
          <Dropdown
            search
            style={{ width: "100%" }}
            selection
            className="sortby_units"
            id="sortby_units"
            name="sortby_units"
            placeholder="Select Units"
            label="Units: "
            onChange={onChangeUnits}
            onOpen={() => handleFloatingDropdownOpen(unitFieldRef)}
            onClose={() => handleFloatingDropdownClose(unitFieldRef)}
            value={building_units}
            error={errors.buildingunits}
            options={selected_building?.units}
            // disabled={
            //   SingleCriticalData?.status === "ongoing" ||
            //   (SingleCriticalData?.status === "supervisor" &&
            //     selected_building?.value)
            //     ? false
            //     : true
            // }
            disabled={true}
            selectOnBlur={false}
          />
        </div>
        <div className="modal-field" ref={pestFieldRef}>
          <label htmlFor="pest_name">Pest: </label>
          <Dropdown
            search
            style={{ width: "100%" }}
            selection
            className="pest_name"
            id="pest_name"
            name="pest_name"
            placeholder={
              SelectedPestData ||
              SingleCriticalData?.status === "ongoing" ||
              SingleCriticalData?.status === "supervisor"
                ? "Select Pest"
                : "No Pest Found"
            }
            label="Pests: "
            onChange={onChangePest}
            onOpen={() => handleFloatingDropdownOpen(pestFieldRef)}
            onClose={() => handleFloatingDropdownClose(pestFieldRef)}
            error={errors.pest}
            value={SelectedPestData}
            options={pestData}
            // disabled={
            //   SingleCriticalData?.status === "ongoing" ||
            //   SingleCriticalData?.status === "supervisor"
            //     ? false
            //     : true
            // }

            disabled={true}
            selectOnBlur={false}
          />
        </div>
        <div className="modal-field">
          <label htmlFor="dateReportedDate">Date Reported: </label>
          <SemanticDatepicker
            style={{ width: "100%" }}
            id="dateReportedDate"
            name="dateReportedDate"
            placeholder={"Date Reported"}
            fluid
            value={currentDate}
            error={errors?.dateReportedDate}
            disabled={
              SingleCriticalData?.status === "ongoing" ||
              SingleCriticalData?.status === "supervisor"
                ? false
                : true
            }
            onChange={onChange}
          />
        </div>
        <div className="modal-field">
          <label htmlFor="lastScheduledDate">
            Last Scheduled Service Date:{" "}
          </label>
          <SemanticDatepicker
            style={{ width: "100%" }}
            id="lastScheduledDate"
            name="lastScheduledDate"
            placeholder={"Date"}
            required={true}
            fluid
            value={ServiceDate}
            error={errors?.lastScheduledDate}
            // disabled={
            //   SingleCriticalData?.status === "ongoing" ||
            //   SingleCriticalData?.status === "supervisor"
            //     ? false
            //     : true
            // }
            disabled={true}
            onChange={onChangeLastServiceDate}
          />
        </div>

        {/* <div className="modal-field">
          <label htmlFor="tech_reported">Tech Reported: </label>
          <Form.Field
            id="tech_reported"
            name="tech_reported"
            placeholder="Tech Reported"
            style={{ width: "100%" }}
            control={Input}
            required={true}
            value={var_critical_info?.tech_reported}
            error={errors["tech_reported"]}
            disabled={
              SingleCriticalData?.status === "ongoing" ||
              SingleCriticalData?.status === "supervisor"
                ? false
                : true
            }
            onChange={onChangeInput}
          />
        </div> */}

        <div className="modal-field modal-field-full">
          <label htmlFor="issue_tags">Issue Tags: </label>
          <Dropdown
            multiple
            selection
            search
            style={{ width: "100%" }}
            className="issue_tags"
            id="issue_tags"
            name="issue_tags"
            placeholder="Select Issue Tags"
            noResultsMessage="All tags selected"
            onChange={onChangeIssueTags}
            value={var_critical_info?.issue_tags || []}
            options={issueTagsOptions.map((opt) => ({
              ...opt,
              content: (
                <span style={{ display: "flex", alignItems: "center" }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: "12px",
                      height: "12px",
                      borderRadius: "2px",
                      backgroundColor: opt.color || "#e0e0e0",
                      border: "1px solid rgba(0,0,0,0.15)",
                      marginRight: "8px",
                    }}
                  />
                  {opt.text}
                </span>
              ),
            }))}
            renderLabel={(item) => ({
              content: item.text,
              style: {
                backgroundColor: item.color || "#e0e0e0",
                color: "#333",
              },
            })}
            disabled={
              SingleCriticalData?.status === "ongoing" ||
              SingleCriticalData?.status === "supervisor"
                ? false
                : true
            }
            selectOnBlur={false}
          />
        </div>

        {/* <div className="modal-field modal-field-full">
          <label htmlFor="summary">Summary of Issue: </label>
          <Form.Field>
            <Form.TextArea
              id="summary"
              style={{
                width: "100%",
                // height: "40px",
                borderColor: errors["summary"] ? "red" : "black",
                backgroundColor: errors["summary"] ? "#fff6f6" : "white",
              }}
              name="summary"
              required={true}
              value={var_critical_info?.summary}
              error={errors["summary"]}
              disabled={
                SingleCriticalData?.status === "ongoing" ||
                SingleCriticalData?.status === "supervisor"
                  ? false
                  : true
              }
              onChange={onChangeInput}
            />
            {renderCharLimitHint("summary")}
          </Form.Field>
        </div> */}

        {/* <div className="modal-field modal-field-full">
          <label htmlFor="csr_actions">CSR's Action: </label>
          <Form.Field>
            <Form.TextArea
              id="csr_actions"
              style={{
                width: "100%",
                // height: "40px",
                borderColor: errors["csr_actions"] ? "red" : "black",
                backgroundColor: errors["csr_actions"] ? "#fff6f6" : "white",
              }}
              name="csr_actions"
              required={true}
              value={var_critical_info?.csr_actions}
              error={errors["csr_actions"]}
              disabled={
                SingleCriticalData?.status === "ongoing" ||
                SingleCriticalData?.status === "supervisor"
                  ? false
                  : true
              }
              onChange={onChangeInput}
            />
            <div
              style={{
                textAlign: "right",
                color:
                  var_critical_info?.csr_actions?.length > 500 ? "red" : "gray",
              }}
            >
              {errors["csr_actions"]
                ? 0
                : 500 - (var_critical_info?.csr_actions?.length || 0)}{" "}
              characters left
            </div>
          </Form.Field>
        </div> */}
        {/* <div className="modal-field modal-field-full">
          <label htmlFor="ss_rm_steps">SS/RM Next Step: </label>
          <Form.Field>
            <Form.TextArea
              id="ss_rm_steps"
              style={{
                width: "100%",
                // height: "40px"
              }}
              name="ss_rm_steps"
              required={true}
              value={var_critical_info?.ss_rm_steps}
              error={errors["ss_rm_steps"]}
              disabled={
                SingleCriticalData?.status === "ongoing" ||
                SingleCriticalData?.status === "supervisor"
                  ? false
                  : true
              }
              onChange={onChangeInput}
            />
            <div
              style={{
                textAlign: "right",
                color:
                  var_critical_info?.ss_rm_steps?.length > 500 ? "red" : "gray",
              }}
            >
              {errors["ss_rm_steps"]
                ? 0
                : 500 - (var_critical_info?.ss_rm_steps?.length || 0)}{" "}
              characters left
            </div>
          </Form.Field>
        </div> */}

        <div className="modal-field modal-field-full">
          <label htmlFor="note">Note: </label>
          <Form.Field>
            <Form.TextArea
              id="note"
              style={{
                width: "100%",
                borderColor: errors["note"] ? "red" : "black",
                backgroundColor: errors["note"] ? "#fff6f6" : "white",
              }}
              name="note"
              value={var_critical_info?.note}
              onChange={onChangeInput}
              disabled={
                SingleCriticalData?.status === "ongoing" ||
                SingleCriticalData?.status === "supervisor"
                  ? false
                  : true
              }
              error={errors["note"]}
            />
            {renderCharLimitHint("note")}
          </Form.Field>
        </div>

        <div className="modal-field modal-field-full">
          <label htmlFor="last_tech_comment">Last Tech Comment: </label>
          <Form.Field>
            <Form.TextArea
              id="last_tech_comment"
              width={20}
              style={{
                width: "100%",
                height: "110px",
                borderColor: errors["last_tech_comment"] ? "red" : "black",
                backgroundColor: errors["last_tech_comment"]
                  ? "#fff6f6"
                  : "white",
              }}
              name="last_tech_comment"
              placeholder="Last Tech Comment"
              value={var_critical_info?.last_tech_comment}
              error={errors["last_tech_comment"]}
              disabled={
                SingleCriticalData?.status === "ongoing" ||
                SingleCriticalData?.status === "supervisor"
                  ? false
                  : true
              }
              onChange={onChangeInput}
            />
            {renderCharLimitHint("last_tech_comment")}
          </Form.Field>
        </div>
      </div>
      <div className="modal-footer">
        {SingleCriticalData?.status === "ongoing" && !var_mdl_progress && (
          <>
            <Button
              type="submit"
              color="green"
              onClick={() => handleSendToSupervisor()}
              loading={isLoading}
            >
              Escalated to S.W.A.T
            </Button>
            <Button
              type="submit"
              color="blue"
              onClick={() => UpdateCriticalData()}
              loading={isLoading}
            >
              Update
            </Button>
            <Button
              type="submit"
              color="red"
              onClick={() => setDeleteModalOpen(true)}
              loading={isLoading}
            >
              Delete
            </Button>
          </>
        )}

        {SingleCriticalData?.status === "resolved" && !var_mdl_progress && (
          <>
            <Button
              type="submit"
              color="blue"
              onClick={() => handleUpdateCriticalUnitStatus()}
              loading={isLoading}
            >
              Restore
            </Button>
          </>
        )}

        {SingleCriticalData.status === "supervisor" && !var_mdl_progress && (
          <>
            {" "}
            <Button
              type="submit"
              color="blue"
              onClick={() => UpdateCriticalData()}
              loading={isLoading}
            >
              Update
            </Button>
            <Button
              type="submit"
              color="orange"
              onClick={() => handleResolveCriticalUnit()}
              loading={isLoading}
            >
              Resolved
            </Button>
            <Button
              type="submit"
              color="green"
              onClick={() => handleUpdateCriticalUnitStatus()}
              loading={isLoading}
            >
              Completed
            </Button>
          </>
        )}
      </div>

      {/***** MODAL: Delete **************************************************************************/}
      <Modal
        id="mdl-sync-missing-order"
        size="tiny"
        dimmer={"inverted"}
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
        }}
      >
        <MDL_DELETE_CRITICAL_UNIT
          unitDetails={SingleCriticalData}
          deleteOptions={deleteReasons}
          onCancel={() => {
            setDeleteModalOpen(false);
          }}
          onSubmit={handleDeleteCriticalUnit}
        ></MDL_DELETE_CRITICAL_UNIT>
      </Modal>

      {/***** END MODAL: Delete **************************************************************************/}
      {/***** MODAL: PROGRESS **************************************************************************/}
      <Modal
        id="mdl-progress"
        dimmer={"inverted"}
        open={var_mdl_progress}
        closeOnDimmerClick={false}
        closeOnEscape={false}
        onClose={() => {}}
      >
        <MDL_PROGRESS
          set_mdl_open={set_mdl_progress}
          var_modaltitle="Please wait"
          var_message={"Stay on this page until this process finish."}
        ></MDL_PROGRESS>
      </Modal>

      {/***** END MODAL: PROGRESS **************************************************************************/}
    </>
  );
};
export default MDL_UPDATE_CRITICAL_UNIT;
